import React, { useState, useEffect, useRef } from 'react';
import { Package, Layers, Info } from 'lucide-react';
import { productoActividadService } from '../services/productoActividadService';
import { ProductoActividad } from '../lib/supabase';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { MoneyInput } from './ui/MoneyInput';
import { DialogShell } from './ui/DialogShell';
import { ModalHeader } from './ui/ModalHeader';
import { TextInput } from './ui/TextInput';
import { reportarErrorInesperado } from '../utils/reportarErrorInesperado';
import { FormErrorAlert } from './ui/FormErrorAlert';

interface ProductoActividadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  actividadId: string;
  producto?: ProductoActividad;
  actividadNombre?: string;
  esPrimerProducto?: boolean;
  cantidadLotes?: number;
}

export const ProductoActividadModal: React.FC<ProductoActividadModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  actividadId,
  producto,
  actividadNombre = '',
  esPrimerProducto = false,
  cantidadLotes = 0
}) => {
  const [loading, setLoading] = useState(false);

  // Hook centralizado para manejo de errores
  const { error, clearError, handleError } = useErrorHandler();

  const [formData, setFormData] = useState({
    nombre_producto: '',
    precio_unitario: 0,
    stock_inicial: 0
  });

  // Función para detectar si hay cambios sin guardar
  const hasFormChanges = () => {
    if (!producto) {
      // Si es un nuevo producto, verificar si hay datos en el formulario
      return formData.nombre_producto !== '' || formData.precio_unitario > 0 || formData.stock_inicial > 0;
    }
    // Si es edición, verificar si los datos cambiaron
    return (
      formData.nombre_producto !== producto.nombre_producto ||
      formData.precio_unitario !== producto.precio_unitario ||
      formData.stock_inicial !== producto.stock_inicial
    );
  };

  // El aviso de "cambios sin guardar" lo gestiona DialogShell vía hasUnsavedChanges
  const hasUnsavedChanges = loading || error !== '' || hasFormChanges();

  useEffect(() => {
    if (producto) {
      setFormData({
        nombre_producto: producto.nombre_producto,
        precio_unitario: producto.precio_unitario,
        stock_inicial: producto.stock_inicial
      });
    } else {
      setFormData({
        nombre_producto: esPrimerProducto ? actividadNombre : '',
        precio_unitario: 0,
        stock_inicial: 0
      });
    }
    handleError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleError se omite a propósito: incluirlo re-ejecutaría el efecto y reiniciaría el formulario
  }, [producto, isOpen, esPrimerProducto, actividadNombre]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    handleError('');
    setLoading(true);

    try {
      if (producto) {
        await productoActividadService.update(producto.id, formData);
      } else {
        await productoActividadService.create({
          ...formData,
          actividad_id: actividadId
        });
      }
      onSuccess();
      onClose();
    } catch (err) {
      reportarErrorInesperado(err, 'No pudimos guardar el producto.');
    } finally {
      setLoading(false);
    }
  };

  const closeRef = useRef<(() => void) | null>(null);
  const requestClose = () => closeRef.current?.();

  if (!isOpen) return null;

  let submitLabel = 'Guardando...';
  if (!loading) submitLabel = producto ? 'Actualizar' : 'Agregar Producto';

  return (
    <DialogShell
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      hasUnsavedChanges={hasUnsavedChanges}
      closeRef={closeRef}
      header={(
        <ModalHeader
          variant="gradient-blue"
          title={producto ? 'Editar Producto' : 'Nuevo Producto'}
          subtitle={producto ? 'Actualiza la información' : 'Agrega un producto a la actividad'}
          icon={<Package className="w-6 h-6" />}
          onClose={requestClose}
        />
      )}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
          <FormErrorAlert error={error} onClear={clearError} />

          <TextInput
            label="Nombre del Producto"
            value={formData.nombre_producto}
            onChange={(v) => setFormData({ ...formData, nombre_producto: v })}
            placeholder="Ej: Pan amasado, Entrada show, etc."
            required
          />

          <div>
            <TextInput
              label="Stock Inicial"
              type="number"
              value={String(formData.stock_inicial)}
              onChange={(v) => setFormData({ ...formData, stock_inicial: Number(v) })}
              min={1}
              step={1}
              placeholder="1"
              required
            />

            {/* Aviso sobre lotes */}
            {producto && cantidadLotes > 0 && (
              <div className="mt-3 bg-purple-50 border border-purple-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <Layers className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-purple-800">
                      Este producto tiene {cantidadLotes} {cantidadLotes === 1 ? 'lote' : 'lotes'}.
                    </p>
                    <p className="text-sm text-purple-700 mt-1">
                      Para aumentar unidades, se recomienda crear un nuevo lote en la pestaña <span className="font-semibold">Lotes</span>.
                    </p>
                    <div className="mt-2 pt-2 border-t border-purple-200">
                      <p className="text-xs text-purple-500 leading-relaxed">
                        <span className="font-medium">¿Cuándo crear lotes?</span><br />
                        ✓ Se fabrica el mismo producto en diferentes tandas (ej: Empanadas Enero, Empanadas Febrero)
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {producto && cantidadLotes === 0 && (
              <div className="mt-3 bg-blue-50 border border-blue-100 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-blue-600 leading-relaxed">
                      Si necesitas producir más unidades del mismo producto en el futuro, considera usar <span className="font-medium">lotes de producción</span> en la pestaña <span className="font-semibold">Lotes</span>.
                    </p>
                    <div className="mt-1.5 pt-1.5 border-t border-blue-100">
                      <p className="text-xs text-blue-400 leading-relaxed">
                        <span className="font-medium">¿Cuándo crear lotes?</span><br />
                        ✓ Se fabrica el mismo producto en diferentes tandas (ej: Empanadas Enero, Empanadas Febrero)
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div>
            <MoneyInput
              label="Precio Unitario"
              required
              value={formData.precio_unitario}
              onChange={(val) => setFormData({ ...formData, precio_unitario: val })}
              min={0}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={requestClose}
              className="flex-1 px-6 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-semibold"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitLabel}
            </button>
          </div>
      </form>
    </DialogShell>
  );
};
